// Tells the server each page someone opens in the app, for Meta's PageView (Edward, Oct 27:
// "everyone gets the page view pixel too"). The server decides everything else: whether ads are
// allowed for them, which pixels, and what Meta sees (a pattern like /take, never an id). Once
// per page (a re-render or StrictMode's second run isn't a new page), never for the admin, and
// again for the page they're on when they accept the cookie banner.

import { useEffect, useRef } from "react";
import { useLocation } from "react-router";

export const CONSENT_ACCEPTED_EVENT = "gp:consent-accepted";

/** This tab's random id, so the server can tell one phone from the next at a venue (same IP, same model). */
function tabId(): string {
  try {
    let id = sessionStorage.getItem("gp_tab");
    if (!id) {
      id = Array.from(crypto.getRandomValues(new Uint8Array(12)), (b) => b.toString(16).padStart(2, "0")).join("");
      sessionStorage.setItem("gp_tab", id);
    }
    return id;
  } catch {
    return "";
  }
}

function send(path: string) {
  // Not under the test runner: screens' tests count the requests they make.
  if (import.meta.env.MODE === "test") return;
  try {
    void fetch("/v1/page-view", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ path, tab: tabId() }),
      credentials: "same-origin",
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // Never in the way of the page.
  }
}

export function usePageViews() {
  const { pathname } = useLocation();
  const last = useRef<string | null>(null);
  useEffect(() => {
    if (pathname === last.current || pathname.startsWith("/admin")) return;
    last.current = pathname;
    send(pathname);
  }, [pathname]);
  useEffect(() => {
    const again = () => {
      if (last.current) send(last.current);
    };
    window.addEventListener(CONSENT_ACCEPTED_EVENT, again);
    return () => window.removeEventListener(CONSENT_ACCEPTED_EVENT, again);
  }, []);
}

export function PageViews() {
  usePageViews();
  return null;
}
